import {useMemo} from 'react';
import {ArrowLeft, Check, Loader2} from 'lucide-react';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {trpc} from '@common-web/lib/trpc';
import {WizardState} from './types';
import {WIZARD_STEPS, billingCycleLabel} from './utils';

export function StepReview({
  state,
  clientId,
  isValid,
  isSubmitting,
  onBack,
  onSubmit,
}: {
  state: WizardState;
  clientId: string;
  isValid: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const catalogQuery = trpc.internal.groupPlan.tenantPlanCatalog.useQuery(
    {clientId},
    {enabled: !!clientId},
  );
  const isVba = catalogQuery.data?.planSource === 'vba';

  const configurationsQuery = trpc.internal.groupPlan.configuration.list.useQuery(
    {clientId},
    {enabled: !!clientId && catalogQuery.data?.planSource === 'postgres'},
  );

  const configurations = configurationsQuery.data?.configurations ?? [];

  const selectedVariations = useMemo(
    () =>
      configurations.flatMap((config) =>
        config.variations
          .filter((v) => state.selectedPlanVariationIds.includes(v.id))
          .map((v) => ({...v, configName: config.name})),
      ),
    [configurations, state.selectedPlanVariationIds],
  );

  const selectedVbaPlans = useMemo(
    () =>
      (catalogQuery.data?.planSource === 'vba' ? catalogQuery.data.plans : []).filter((p) =>
        state.selectedVbaPlanIds.includes(p.planId),
      ),
    [catalogQuery.data, state.selectedVbaPlanIds],
  );

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardContent className='p-6'>
        <h3 className='text-lg font-semibold text-brand-navy mb-1'>{WIZARD_STEPS[3].label}</h3>
        <p className='text-sm text-muted-foreground mb-6'>
          Confirm all details before creating the group.
        </p>

        <div className='grid grid-cols-2 gap-x-8 gap-y-6'>
          <div>
            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2'>
              Agent
            </p>
            {state.selectedAgent ? (
              <div className='space-y-0.5 text-sm'>
                <p className='font-medium text-gray-900'>{state.selectedAgent.fullName}</p>
                {state.selectedAgent.admin123AgentId && (
                  <p className='text-gray-500'>ID: {state.selectedAgent.admin123AgentId}</p>
                )}
                {state.selectedAgent.email && (
                  <p className='text-gray-500'>{state.selectedAgent.email}</p>
                )}
              </div>
            ) : (
              <p className='text-sm text-gray-400'>No agent selected</p>
            )}
          </div>

          <div>
            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2'>
              Group
            </p>
            <div className='space-y-0.5 text-sm'>
              <p className='font-medium text-gray-900'>{state.groupName || '—'}</p>
              {state.federalTaxId && <p className='text-gray-500'>FEIN: {state.federalTaxId}</p>}
              <p className='text-gray-500'>
                {state.isAnalyzerGroup ? 'Analyzer Group' : 'Standard Group'}
              </p>
            </div>
          </div>

          <div>
            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2'>
              Address
            </p>
            <div className='space-y-0.5 text-sm text-gray-500'>
              {state.addressLine1 && <p>{state.addressLine1}</p>}
              {state.addressLine2 && <p>{state.addressLine2}</p>}
              <p>
                {[state.city, state.stateCode, state.zipCode].filter(Boolean).join(', ') || '—'}
              </p>
            </div>
          </div>

          <div>
            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2'>
              Primary Contact
            </p>
            <div className='space-y-0.5 text-sm'>
              <p className='font-medium text-gray-900'>
                {state.firstName} {state.lastName}
              </p>
              {state.email && <p className='text-gray-500'>{state.email}</p>}
              {state.phone && <p className='text-gray-500'>{state.phone}</p>}
            </div>
          </div>

          <div>
            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2'>
              Dates & Billing
            </p>
            <div className='space-y-0.5 text-sm text-gray-500'>
              <p>Effective: {state.originalDate || '—'}</p>
              <p>Open Enrollment: {state.openEnrollmentDate || '—'}</p>
              <p>Billing Cycle: {billingCycleLabel(state.billingCycleId) || '—'}</p>
            </div>
          </div>

          <div>
            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2'>
              Products
            </p>
            {isVba ? (
              selectedVbaPlans.length > 0 ? (
                <div className='space-y-1 text-sm'>
                  {selectedVbaPlans.map((p) => (
                    <div key={p.planId} className='flex items-center justify-between'>
                      <span className='text-gray-700'>{p.description}</span>
                      <span className='text-gray-400 text-xs font-mono'>{p.planId}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-sm text-gray-400'>None selected</p>
              )
            ) : selectedVariations.length > 0 ? (
              <div className='space-y-1 text-sm'>
                {selectedVariations.map((v) => (
                  <div key={v.id} className='flex items-center justify-between'>
                    <span className='text-gray-700'>{v.name}</span>
                    {v.maxPayoutPEPM != null && (
                      <span className='text-gray-400 text-xs'>
                        ${v.maxPayoutPEPM.toString()} PEPM
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-sm text-gray-400'>None selected</p>
            )}
          </div>
        </div>

        <div className='flex items-center justify-between mt-6 pt-4 border-t'>
          <Button variant='outline' onClick={onBack} disabled={isSubmitting}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!isValid || isSubmitting}
            className='bg-emerald-600 hover:bg-emerald-700 text-white'
          >
            {isSubmitting ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Creating Group...
              </>
            ) : (
              <>
                <Check className='h-4 w-4 mr-2' />
                Create Group
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
